/**
 * Import modal — thin render shell for collection imports.
 *
 * All state and logic live in useCollectionImport.
 * This component only renders the 4-step UI:
 *   input → preview → importing → complete
 */
import type { ReactElement } from 'react';

import { FaArrowRight, FaCheckCircle, FaFileImport, FaInfoCircle } from 'react-icons/fa';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import Spinner from '@/components/Spinner/Spinner';

import useCollectionImport from '../hooks/useCollectionImport';
import FileDropzone from './FileDropzone';
import FolderDestinationSelector from './FolderDestinationSelector';
import ImportFieldSummaryComponent from './ImportFieldSummary';
import ImportModeSelector from './ImportModeSelector';
import ImportPreviewTable from './ImportPreviewTable';
import ImportTabs from './ImportTabs';
import PrintingPreferenceToggle from './PrintingPreferenceToggle';
import SourceSelector from './SourceSelector';
import SourceSystemDropdown from './SourceSystemDropdown';

// ============================================================================
// Constants
// ============================================================================

/** Example placeholder text for each import source */
const SOURCE_PLACEHOLDERS: Record<string, string> = {
  dragonshield: `Paste Dragon Shield CSV export here...

Example:
Folder Name,Name,Set Code,Set Name,Collector Number,Lang,Finish,Qty
"Main","Lightning Bolt","M10","Magic 2010","146","en","nonfoil","4"`,

  delverlens: `Paste DelverLens CSV export here (current format)...

Required columns:
Name,Edition code,Collector's number,Quantity,Foil/Etched,Condition,Language,List name,Delver Local ID

Optional (captured as external IDs):
Scryfall ID,MultiverseID,TCGPlayer productID,CardMarket ID,Acquired Price,Currency

Example:
Name,List name,Edition code,Collector's number,Quantity,Foil/Etched,Condition,Language,Scryfall ID,Delver Local ID
"Lightning Bolt","Modern","M10","146","4","","Near Mint","English","e4f8f6d2-...","1234"`,

  moxfield: `Paste Moxfield CSV export here...

Example:
Count,Name,Edition,Collector Number,Foil
4,"Lightning Bolt","M10","146","No"`,

  archidekt: `Paste MTGA/Archidekt format here...

Example:
4 Lightning Bolt (M10) 146
2 Counterspell (MH2) 46`,

  manabox: `Paste ManaBox export here...

Example (text):
4 Lightning Bolt (M10) 146

Example (CSV):
Name,Set,Number,Quantity
"Lightning Bolt","M10","146","4"`,

  default: `Paste your card list here...

Example MTGA format:
4 Lightning Bolt (M10) 146
2 Counterspell (MH2) 46

Or paste CSV content directly.`,
};

// ============================================================================
// Types
// ============================================================================

export interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ============================================================================
// Component
// ============================================================================

export default function ImportModal({ isOpen, onClose }: ImportModalProps): ReactElement {
  const state = useCollectionImport(onClose);

  const {
    steps,
    activeTab,
    setActiveTab,
    pasteText,
    setPasteText,
    selectedSource,
    setSelectedSource,
    matchedCards,
    parseErrors,
    isMatching,
    fieldSummary,
    matchingStrategy,
    importMode,
    setImportMode,
    folderDestination,
    setFolderDestination,
    useImportFolders,
    setUseImportFolders,
    duplicateStats,
    printingPreference,
    setPrintingPreference,
    hasImportFolders,
    effectiveSource,
    detectedSource,
    crossImportInfo,
    importProgress,
    importResult,
    overwriteContext,
    importableCount,
    importButtonText,
    folders,
    resetState,
    handleClose,
    handleFileDrop,
    handlePasteSubmit,
    handlePreviewSourceChange,
    handleImport,
  } = state;

  // Get placeholder based on selected source
  const placeholderText = selectedSource ? SOURCE_PLACEHOLDERS[selectedSource] || SOURCE_PLACEHOLDERS.default : SOURCE_PLACEHOLDERS.default;

  // Upload tab content
  const uploadContent = (
    <div className="space-y-4">
      <SourceSelector value={selectedSource} onChange={setSelectedSource} detectedSource={detectedSource} />
      <FileDropzone onFileDrop={handleFileDrop} disabled={isMatching} accept=".csv,.txt,.json" />
    </div>
  );

  // Paste tab content
  const pasteContent = (
    <div className="space-y-4">
      <SourceSelector value={selectedSource} onChange={setSelectedSource} detectedSource={detectedSource} />
      <textarea
        value={pasteText}
        onChange={(e) => setPasteText(e.target.value)}
        placeholder={placeholderText}
        rows={10}
        className="border-storm bg-slate text-pearl placeholder:text-ash focus-ring w-full resize-none rounded-lg border p-4 font-mono text-sm"
        disabled={isMatching}
      />
      <div className="flex justify-end">
        <Button text="Process Import" icon={<FaArrowRight />} onClick={handlePasteSubmit} disabled={!pasteText.trim() || isMatching} />
      </div>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import Cards" size="xl" showCloseButton={steps.currentStep !== 'importing'}>
      {/* Step: Input */}
      {steps.isStep('input') && (
        <div className="relative space-y-4">
          {isMatching && (
            <div className="bg-obsidian/90 absolute inset-0 z-20 flex flex-col items-center justify-center rounded-xl backdrop-blur-sm">
              <Spinner size="lg" />
              <p className="text-pearl mt-4 text-lg font-medium">Processing import...</p>
              <p className="text-ash mt-2 text-sm">Matching cards to database</p>
            </div>
          )}

          <ImportTabs activeTab={activeTab} onTabChange={setActiveTab} uploadContent={uploadContent} pasteContent={pasteContent} />

          {parseErrors.length > 0 && !isMatching && (
            <div className="bg-danger/10 border-danger/30 rounded-lg border p-4">
              <p className="text-danger mb-2 font-medium">Import Errors</p>
              <ul className="text-danger/80 list-inside list-disc space-y-1 text-sm">
                {parseErrors.map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Step: Preview */}
      {steps.isStep('preview') && (
        <div className="space-y-6">
          {crossImportInfo && (
            <div className="bg-aether/10 border-aether/30 flex items-start gap-3 rounded-lg border p-3">
              <FaInfoCircle className="text-aether mt-0.5 h-4 w-4 shrink-0" />
              <p className="text-silver text-sm">{crossImportInfo}</p>
            </div>
          )}

          {fieldSummary && <ImportFieldSummaryComponent summary={fieldSummary} />}

          <ImportPreviewTable cards={matchedCards} importMode={importMode} hasDuplicateDetection={matchingStrategy !== 'none'} />

          <div className="grid gap-6 md:grid-cols-2">
            <div className="flex flex-col gap-6">
              <ImportModeSelector
                value={importMode}
                onChange={setImportMode}
                matchingStrategy={matchingStrategy}
                duplicateCount={duplicateStats.duplicate}
                newCount={duplicateStats.new}
                overwriteContext={overwriteContext}
              />
            </div>
            <div className="flex flex-col gap-6">
              <SourceSystemDropdown value={effectiveSource} onChange={handlePreviewSourceChange} detectedSource={detectedSource} />
              <FolderDestinationSelector
                value={folderDestination}
                onChange={setFolderDestination}
                useImportFolders={useImportFolders}
                onUseImportFoldersChange={setUseImportFolders}
                hasImportFolders={hasImportFolders}
                folders={folders}
              />
              <PrintingPreferenceToggle value={printingPreference} onChange={setPrintingPreference} />
            </div>
          </div>

          <div className="border-storm flex items-center justify-between border-t pt-4">
            <Button text="Back" variant="text" onClick={() => steps.goTo('input')} />
            <div className="flex items-center gap-3">
              <span className="text-ash text-sm">
                {importMode === 'update' && duplicateStats.duplicate > 0
                  ? `${duplicateStats.new} new, ${duplicateStats.duplicate} to update`
                  : `${importableCount} cards ready`}
              </span>
              <Button
                text={importButtonText}
                icon={<FaFileImport />}
                onClick={handleImport}
                disabled={importableCount === 0}
                color="create"
              />
            </div>
          </div>
        </div>
      )}

      {/* Step: Importing */}
      {steps.isStep('importing') && (
        <div className="flex flex-col items-center justify-center py-12">
          <Spinner size="lg" />
          <p className="text-pearl mt-4 text-lg font-medium">
            {importMode === 'overwrite' && importProgress.current === 0 ? 'Removing existing cards...' : 'Importing cards...'}
          </p>
          <p className="text-ash mt-2">
            {importProgress.current} of {importProgress.total} operations
          </p>
          <div className="bg-slate mt-4 h-2 w-64 overflow-hidden rounded-full">
            <div
              className="bg-aether h-full transition-all duration-300"
              style={{
                width: `${importProgress.total > 0 ? (importProgress.current / importProgress.total) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Step: Complete */}
      {steps.isStep('complete') && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="bg-success/20 mb-4 rounded-full p-4">
            <FaCheckCircle className="text-success h-12 w-12" />
          </div>
          <h3 className="text-pearl text-xl font-semibold">Import Complete!</h3>
          {importResult.success > 0 && (
            <p className="text-silver mt-2">
              Added {importResult.success} new card{importResult.success !== 1 ? 's' : ''}
            </p>
          )}
          {importResult.updated > 0 && (
            <p className="text-aether mt-1">
              Updated {importResult.updated} existing card{importResult.updated !== 1 ? 's' : ''}
            </p>
          )}
          {importResult.failed > 0 && (
            <p className="text-danger mt-1 text-sm">
              {importResult.failed} card{importResult.failed !== 1 ? 's' : ''} failed to import
            </p>
          )}
          <div className="mt-6 flex gap-3">
            <Button text="Import More" variant="outlined" onClick={resetState} />
            <Button text="Done" onClick={handleClose} />
          </div>
        </div>
      )}
    </Modal>
  );
}
